import { CurrencyContext, LocaleContext } from '@tock/coretypes';
import { getDecimalSeparator, getGroupSeparator, isDecimalCurrency } from '@tock/money';
import NumericField, { NumericFieldProps } from 'ui/NumericField';
import React, { FunctionComponent, useContext } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';

/**
 * Interface to the PriceField Component.
 */
export interface PriceFieldProps
  extends Omit<NumericFieldProps, 'decimalScale' | 'thousandSeparator' | 'decimalSeparator'> {
  /**
   * defaults to current context currency
   */
  currency?: string;
  /**
   * defaults to current context locale
   */
  locale?: string;
  /**
   * Defaults to currency code symbol if undefined
   */
  prefix?: string;
  /**
   * either 0 or 2, default is 2
   */
  decimalScale?: 0 | 2;
}

/**
 * PriceField is a wrapper around NumericField that uses
 * context to set sensible defaults for decimal places and
 * a prefix for the currency symbol. All of these defaults can be overridden
 * with props if desired, except of course decimal places in the event
 * that a currency does not have decimals.
 *
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const PriceField: FunctionComponent<React.PropsWithChildren<PriceFieldProps>> = ({
  allowNegative = false,
  fixedDecimalScale = false,
  decimalScale = 2,
  prefix,
  currency,
  locale,
  ...rest
}) => {
  const contextCurrency = useContext(CurrencyContext);
  const contextLocale = useContext(LocaleContext);
  const realLocale = locale ?? contextLocale;
  const realCurrency = currency ?? contextCurrency;
  const defaultPrefix = getSymbolFromCurrency(realCurrency);
  const realPrefix = prefix ?? defaultPrefix;
  const hasDecimals = isDecimalCurrency(realCurrency);
  const realDecimalScale = hasDecimals ? decimalScale : 0;

  const decimalSeparator = getDecimalSeparator(realLocale);
  const groupSeparator = getGroupSeparator(realLocale);

  return (
    <NumericField
      {...rest}
      allowNegative={allowNegative}
      decimalScale={realDecimalScale}
      decimalSeparator={decimalSeparator}
      fixedDecimalScale={fixedDecimalScale}
      thousandSeparator={groupSeparator}
      prefix={realPrefix} // see note in numeric field regarding pending prefix decision
    />
  );
};

export default PriceField;
